import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDown, ChevronUp, Loader2, TrendingUp } from 'lucide-react';
import { useSelector } from 'react-redux';

import { Badge } from '@design-system';
import { Button } from '@client/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CurrencyInput, PercentInput } from '@client/components/NumericInput';
import { cn } from '@client/lib';
import { formatCurrency, formatLtcCostDisplay } from '@shared';
import { formatCompactCurrency } from '@client/util/formatCompactCurrency';
import { useLtcCostDisplayPrecision } from '@client/hooks/useLtcCostDisplayPrecision';
import { selectClient } from '@client/model/selectors';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useSelfFundingProjectedValue } from '@client/hooks/useSelfFundingProjectedValue';

import { type AssetTypeId } from '@/features/guided-financial-planning/config';
import { useIsSessionComplete } from '@/features/guided-financial-planning/context';
import { useSelfFundingForm } from '@/features/guided-financial-planning/hooks';
import { guidedPlanningApi } from '@/features/guided-financial-planning/api';
import { PartnerAllocationSection, type AllocationOption } from './partner-allocation-section';

type SaveState = 'idle' | 'saving' | 'saved';

interface AssetFieldLabels {
  currentValue: { label: string; placeholder: string };
  yearlyContribution: { label: string; placeholder: string };
  rateOfReturn: { label: string; placeholder: string };
  taxRate: { label: string; placeholder: string };
}

const DEFAULT_FIELD_LABELS: AssetFieldLabels = {
  currentValue: { label: 'CURRENT BALANCE', placeholder: 'Enter current balance' },
  yearlyContribution: { label: 'ASSUMED YEARLY CONTRIBUTION', placeholder: 'Enter yearly amount' },
  rateOfReturn: { label: 'ASSUMED ANNUAL GROWTH', placeholder: 'Enter annual % assumption' },
  taxRate: { label: 'EST. TAX RATE ON WITHDRAWAL', placeholder: 'Enter estimated tax %' },
};

const ASSET_FIELD_LABELS: Partial<Record<AssetTypeId, Partial<AssetFieldLabels>>> = {
  'roth-ira': {
    taxRate: { label: 'EST. TAX RATE ON WITHDRAWAL', placeholder: '0% only if qualified' },
  },
  'roth-401k': {
    taxRate: { label: 'EST. TAX RATE ON WITHDRAWAL', placeholder: '0% only if qualified' },
  },
  'traditional-ira': {
    taxRate: { label: 'EST. INCOME TAX RATE ON WITHDRAWAL', placeholder: 'Enter estimated tax %' },
  },
  'traditional-401k': {
    taxRate: { label: 'EST. INCOME TAX RATE ON WITHDRAWAL', placeholder: 'Enter estimated tax %' },
  },
  hsa: {
    taxRate: { label: 'EST. TAX RATE ON WITHDRAWAL', placeholder: '0% if qualified expense' },
  },
  brokerage: {
    currentValue: { label: 'CURRENT ACCOUNT VALUE', placeholder: 'Enter current balance' },
    taxRate: { label: 'EST. TAX RATE ON GAIN', placeholder: 'Enter rate on gains only' },
  },
  'home-equity': {
    currentValue: { label: 'CURRENT AVAILABLE EQUITY', placeholder: 'Enter current home equity' },
    yearlyContribution: { label: 'YEARLY EQUITY ADDED', placeholder: 'Principal paydown or added equity' },
    rateOfReturn: { label: 'ASSUMED HOME VALUE GROWTH', placeholder: 'Enter annual home growth %' },
    taxRate: { label: 'EST. COST / TAX TO ACCESS EQUITY', placeholder: '0% if borrowing; otherwise estimate' },
  },
  'retirement-income': {
    currentValue: { label: 'CURRENT ANNUAL INCOME', placeholder: 'Annual amount available today' },
    yearlyContribution: { label: 'ADDITIONAL YEARLY INCOME', placeholder: 'Annual amount you expect later' },
    rateOfReturn: { label: 'EXPECTED ANNUAL INCREASE', placeholder: 'Enter annual % increase' },
    taxRate: { label: 'EST. TAX RATE ON PAYMENTS', placeholder: 'Enter estimated tax %' },
  },
  cryptocurrency: {
    currentValue: { label: 'CURRENT HOLDINGS VALUE', placeholder: 'Enter current balance' },
    taxRate: { label: 'EST. TAX RATE ON GAIN', placeholder: 'Enter rate on gains only' },
  },
};

function getFieldLabels(assetTypeId: AssetTypeId): AssetFieldLabels {
  const overrides = ASSET_FIELD_LABELS[assetTypeId];
  if (!overrides) return DEFAULT_FIELD_LABELS;
  return {
    currentValue: overrides.currentValue ?? DEFAULT_FIELD_LABELS.currentValue,
    yearlyContribution: overrides.yearlyContribution ?? DEFAULT_FIELD_LABELS.yearlyContribution,
    rateOfReturn: overrides.rateOfReturn ?? DEFAULT_FIELD_LABELS.rateOfReturn,
    taxRate: overrides.taxRate ?? DEFAULT_FIELD_LABELS.taxRate,
  };
}

function LoadingOverlay({ state }: { state: SaveState }) {
  if (state === 'idle') return null;

  return (
    <div
      className={cn(
        'absolute inset-0 z-10 flex scale-105 flex-col items-center justify-center rounded-lg bg-white/90 backdrop-blur-sm transition-opacity duration-500',
        state === 'saved' ? 'opacity-0' : 'opacity-100',
      )}
    >
      {state === 'saving' && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <p className="text-sm font-medium text-gray-700">Saving...</p>
        </div>
      )}
      {state === 'saved' && (
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
            <CheckIcon className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-sm font-medium text-green-600">Saved</p>
        </div>
      )}
    </div>
  );
}

export interface AllocationCallbacks {
  hasPartner: boolean;
  clientName: string;
  partnerName: string;
  initialState: {
    enabled: boolean;
    option: AllocationOption;
    customClientPercent: number;
    originalValue?: number;
  };
  onApply: (
    state: {
      enabled: boolean;
      option: AllocationOption;
      customClientPercent: number;
      formValue?: number;
    },
    overrides?: {
      fundingSourceId: string;
      sourceData: SelfFundingSource;
    },
  ) => Promise<void>;
}

interface AccountCardProps {
  assetTypeId: AssetTypeId;
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  fundingSourceId: string | null;
  existingSource?: SelfFundingSource;
  onSaveComplete: () => void;
  disabled?: boolean;
  allocationAmount?: number;
  lockedByPartnerName?: string;
  allocation?: AllocationCallbacks;
  hideHeader?: boolean;
}

function isSourceComplete(source?: SelfFundingSource): boolean {
  if (!source) return false;
  return (
    source.existingAssetsValue !== null &&
    source.annualRateOfReturn !== null &&
    source.gainsTaxRate !== null
  );
}

export function AccountCard({
  assetTypeId,
  label,
  isExpanded,
  onToggle,
  fundingSourceId,
  existingSource,
  onSaveComplete,
  disabled,
  allocationAmount,
  lockedByPartnerName,
  allocation,
  hideHeader,
}: AccountCardProps) {
  const isLocked = !!lockedByPartnerName;
  const isComplete = isSourceComplete(existingSource);
  const { form, onSubmit, buildSourceData, isSaving, clientId } = useSelfFundingForm(
    assetTypeId,
    fundingSourceId,
    existingSource,
  );
  const client = useSelector(selectClient);
  const watchedValues = useWatch({
    control: form.control,
    name: ['existingAssetsValue', 'yearlyContribution', 'annualRateOfReturn', 'gainsTaxRate'],
  });
  const [existingAssetsValue, yearlyContribution, annualRateOfReturn, gainsTaxRate] =
    watchedValues ?? [];

  const selfFundingStateForPreview = useMemo((): Partial<SelfFundingSource> | null => {
    if (existingAssetsValue == null || annualRateOfReturn == null || gainsTaxRate == null) {
      return null;
    }
    return {
      existingAssetsValue: typeof existingAssetsValue === 'number' ? existingAssetsValue : null,
      monthlyContributionAmount:
        yearlyContribution != null && typeof yearlyContribution === 'number'
          ? yearlyContribution / 12
          : null,
      annualRateOfReturn: typeof annualRateOfReturn === 'number' ? annualRateOfReturn : null,
      gainsTaxRate: typeof gainsTaxRate === 'number' ? gainsTaxRate : null,
      monthlyIncome: existingSource?.monthlyIncome ?? null,
    };
  }, [
    existingAssetsValue,
    yearlyContribution,
    annualRateOfReturn,
    gainsTaxRate,
    existingSource?.monthlyIncome,
  ]);

  const { ltcAtYear, yearsTillLtc, ltcAtAge } = client?.appliedInferenceSet ?? {};
  const clientBirthDate = client?.intakeSurvey?.clientBirthDate;
  const allPhaseCareMonthsNeeded = client?.allPhaseCosts?.allPhaseCareMonthsNeeded;

  const { projectedValue, ageAtLtc } = useSelfFundingProjectedValue(selfFundingStateForPreview, {
    ltcAtYear: ltcAtYear ?? new Date().getFullYear(),
    yearsTillLtc: Math.max(yearsTillLtc ?? 0, 0),
    allPhaseCareMonthsNeeded,
    ltcAtAge: ltcAtAge ?? undefined,
    clientBirthDate: clientBirthDate ?? undefined,
  });

  const { flags } = useFeatureFlags();
  const roundingStep = useLtcCostDisplayPrecision();
  const showPreview = flags.selfFundingGrowthPreview === true && projectedValue != null;

  const currentValueFormatted = formatCompactCurrency(
    typeof existingAssetsValue === 'number' ? existingAssetsValue : null,
  );
  const previewTooltipCopy =
    'Current account value and projected value at predicted long-term care age, based on your growth and tax assumptions.';

  const previewBadgeContent = showPreview ? (
    <>
      <span className="text-gray-700">
        {
          formatLtcCostDisplay(
            typeof existingAssetsValue === 'number' ? existingAssetsValue : undefined,
            { roundingStep },
          ).formattedDisplay
        }
      </span>
      <span className="mx-1"></span>
      <span className="inline-flex items-center font-semibold">
        <TrendingUp className="mr-1 size-3.5 shrink-0" />
        {formatLtcCostDisplay(projectedValue ?? undefined, { roundingStep }).formattedDisplay}
      </span>
      <span className="ml-1 text-gray-700">(age {ageAtLtc ?? ltcAtYear})</span>
    </>
  ) : null;

  const fieldLabels = getFieldLabels(assetTypeId);
  const isSessionComplete = useIsSessionComplete();
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const prevIsSavingRef = useRef(false);
  const saveCompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Allocation section state
  const [allocationEnabled, setAllocationEnabled] = useState(
    allocation?.initialState.enabled ?? false,
  );
  const [allocationOption, setAllocationOption] = useState<AllocationOption>(
    allocation?.initialState.option ?? 'mirror',
  );
  const [customClientPercent, setCustomClientPercent] = useState(
    allocation?.initialState.customClientPercent ?? 62,
  );

  // Sync allocation state when initial state changes (e.g. session reload)
  const prevAllocationKey = useRef<string | null>(null);
  useEffect(() => {
    if (!allocation) return;
    const key = `${fundingSourceId}-${allocation.initialState.enabled}-${allocation.initialState.option}-${allocation.initialState.customClientPercent}`;
    if (prevAllocationKey.current === key) return;
    prevAllocationKey.current = key;
    setAllocationEnabled(allocation.initialState.enabled);
    setAllocationOption(allocation.initialState.option);
    setCustomClientPercent(allocation.initialState.customClientPercent);

    // Show pre-split total in the form so user always edits the full amount
    if (allocation.initialState.enabled && allocation.initialState.originalValue != null) {
      form.setValue('existingAssetsValue', allocation.initialState.originalValue);
    }
  }, [allocation, fundingSourceId, form]);

  // After Redux refreshes the source with the split value, restore the pre-split original
  useEffect(() => {
    if (!allocation?.initialState.enabled) return;
    const originalValue = allocation.initialState.originalValue;
    if (originalValue == null) return;
    if (
      existingSource?.existingAssetsValue != null &&
      existingSource.existingAssetsValue !== originalValue
    ) {
      form.setValue('existingAssetsValue', originalValue);
    }
  }, [
    existingSource?.existingAssetsValue,
    allocation?.initialState.enabled,
    allocation?.initialState.originalValue,
    form,
  ]);

  // Track save state transitions
  useEffect(() => {
    const prevIsSaving = prevIsSavingRef.current;

    if (!prevIsSaving && isSaving) {
      setSaveState('saving');
    } else if (prevIsSaving && !isSaving && saveState === 'saving') {
      setSaveState('saved');

      saveCompleteTimeoutRef.current = setTimeout(() => {
        setSaveState('idle');
        onSaveComplete();
      }, 800);
    }

    prevIsSavingRef.current = isSaving;
  }, [isSaving, saveState, onSaveComplete]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveCompleteTimeoutRef.current) {
        clearTimeout(saveCompleteTimeoutRef.current);
      }
    };
  }, []);

  const [isApplyingAllocation, setIsApplyingAllocation] = useState(false);

  const handleSubmit = useCallback(async () => {
    const isFirstSaveWithAllocation = allocation && !fundingSourceId && allocationEnabled;

    if (isFirstSaveWithAllocation) {
      // Validate form first
      const isValid = await form.trigger();
      if (!isValid) return;

      setSaveState('saving');
      setIsApplyingAllocation(true);
      try {
        const formValues = form.getValues();
        const sourceData = buildSourceData(formValues);

        // Save via API directly so we get the fundingSourceId back
        const response = await guidedPlanningApi.upsertSelfFunding({
          clientId,
          fundingSourceId: null,
          fundingSourceData: sourceData,
        });

        // Apply allocation with the newly created fundingSourceId
        await allocation.onApply(
          {
            enabled: allocationEnabled,
            option: allocationOption,
            customClientPercent,
            formValue: formValues.existingAssetsValue,
          },
          {
            fundingSourceId: response.fundingSourceId,
            sourceData,
          },
        );

        setSaveState('saved');
        setTimeout(() => {
          setSaveState('idle');
          onSaveComplete();
        }, 800);
      } catch {
        setSaveState('idle');
      } finally {
        setIsApplyingAllocation(false);
      }
    } else {
      // Standard flow: save form via Redux, then apply allocation
      const isValid = await form.trigger();
      if (!isValid) return;

      await form.handleSubmit(onSubmit)();

      if (allocation && fundingSourceId && existingSource) {
        setIsApplyingAllocation(true);
        try {
          const formValues = form.getValues();
          const sourceData = buildSourceData(formValues);
          await allocation.onApply(
            {
              enabled: allocationEnabled,
              option: allocationOption,
              customClientPercent,
              formValue:
                typeof formValues.existingAssetsValue === 'number'
                  ? formValues.existingAssetsValue
                  : undefined,
            },
            {
              fundingSourceId,
              sourceData,
            },
          );
        } finally {
          setIsApplyingAllocation(false);
        }
      }
    }
  }, [
    form,
    onSubmit,
    buildSourceData,
    clientId,
    allocation,
    fundingSourceId,
    existingSource,
    allocationEnabled,
    allocationOption,
    customClientPercent,
    onSaveComplete,
  ]);

  const accountValue =
    typeof existingAssetsValue === 'number'
      ? existingAssetsValue
      : (existingSource?.existingAssetsValue ?? 0);

  const formContent = (
    <>
      <div className="relative">
        <LoadingOverlay state={saveState} />

        {showPreview && !hideHeader && (
          <Badge
            variant="secondary"
            className="mb-4 w-fit font-normal sm:hidden"
          >
            {ageAtLtc != null
              ? `Projected value by age ${ageAtLtc} : `
              : `Projected value by ${ltcAtYear} : `}
            <span className="font-semibold">
              {formatLtcCostDisplay(projectedValue ?? undefined, { roundingStep }).formattedDisplay}
            </span>
          </Badge>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Controller
            name="existingAssetsValue"
            control={form.control}
            render={({ field, fieldState }) => (
              <CurrencyInput
                label={fieldLabels.currentValue.label}
                placeholder={fieldLabels.currentValue.placeholder}
                value={field.value ?? null}
                onChange={field.onChange}
                onPreview={field.onChange}
                errorMessage={fieldState.error?.message}
                disabled={disabled}
              />
            )}
          />

          <Controller
            name="yearlyContribution"
            control={form.control}
            render={({ field, fieldState }) => (
              <CurrencyInput
                label={fieldLabels.yearlyContribution.label}
                placeholder={fieldLabels.yearlyContribution.placeholder}
                value={field.value ?? null}
                onChange={field.onChange}
                onPreview={field.onChange}
                errorMessage={fieldState.error?.message}
                disabled={disabled}
              />
            )}
          />

          <Controller
            name="annualRateOfReturn"
            control={form.control}
            render={({ field, fieldState }) => (
              <PercentInput
                label={fieldLabels.rateOfReturn.label}
                placeholder={fieldLabels.rateOfReturn.placeholder}
                value={field.value ?? null}
                onChange={field.onChange}
                onPreview={field.onChange}
                errorMessage={fieldState.error?.message}
                disabled={disabled}
              />
            )}
          />

          <Controller
            name="gainsTaxRate"
            control={form.control}
            render={({ field, fieldState }) => (
              <PercentInput
                label={fieldLabels.taxRate.label}
                placeholder={fieldLabels.taxRate.placeholder}
                value={field.value ?? null}
                onChange={field.onChange}
                onPreview={field.onChange}
                errorMessage={fieldState.error?.message}
                disabled={disabled}
              />
            )}
          />
        </div>
      </div>

      {/* Partner allocation section */}
      {allocation?.hasPartner && (
        <PartnerAllocationSection
          enabled={allocationEnabled}
          onEnabledChange={setAllocationEnabled}
          value={accountValue}
          clientName={allocation.clientName}
          partnerName={allocation.partnerName}
          selectedOption={allocationOption}
          onOptionChange={setAllocationOption}
          customClientPercent={customClientPercent}
          onCustomClientPercentChange={setCustomClientPercent}
          disabled={disabled}
        />
      )}

      {!disabled && (
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSaving || isApplyingAllocation}
          className={cn('mt-4 w-full rounded-full', {
            'rounded-md': hideHeader,
          })}
        >
          {isApplyingAllocation ? 'Saving...' : 'Save'}
        </Button>
      )}
    </>
  );

  if (hideHeader) {
    return <div>{formContent}</div>;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-gray-900">{label}</span>
        </div>

        <div className="flex min-w-0 max-w-[65%] items-center justify-end gap-3">
          {showPreview && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex min-w-0 max-w-full shrink cursor-default">
                  <Badge
                    variant="secondary"
                    className="hidden min-w-0 max-w-full shrink truncate font-normal sm:inline-flex"
                  >
                    {previewBadgeContent}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-xs"
              >
                <p>{previewTooltipCopy}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {!showPreview && isComplete && existingSource?.existingAssetsValue != null && (
            <Badge
              variant="secondary"
              className="hidden font-medium sm:inline-flex"
            >
              {formatCurrency(existingSource.existingAssetsValue)}
              {isSessionComplete && allocationAmount && allocationAmount > 0 && (
                <span className="ml-1 text-gray-500">(−{formatCurrency(allocationAmount)})</span>
              )}
            </Badge>
          )}
          {flags.selfFundingGrowthPreview === true &&
            (showPreview || (isComplete && existingSource?.existingAssetsValue != null)) && (
              <Badge
                variant="secondary"
                className="font-medium sm:hidden"
              >
                {existingSource?.existingAssetsValue != null
                  ? formatCurrency(existingSource.existingAssetsValue)
                  : currentValueFormatted}
                {isSessionComplete &&
                  allocationAmount &&
                  allocationAmount > 0 &&
                  existingSource?.existingAssetsValue != null && (
                    <span className="ml-1 text-gray-500">
                      (−{formatCurrency(allocationAmount)})
                    </span>
                  )}
              </Badge>
            )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </button>

      {/* Content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden border-t border-gray-100"
          >
            {isLocked ? (
              <div className="grid grid-cols-2 gap-3 px-5 pb-5 pt-4">
                <div className="rounded-lg bg-gray-50 px-3 py-2.5">
                  <p className="text-foreground text-xs font-medium uppercase tracking-wide">
                    {fieldLabels.currentValue.label}
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-gray-950">
                    {existingSource?.existingAssetsValue != null
                      ? formatCurrency(existingSource.existingAssetsValue)
                      : '—'}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 px-3 py-2.5">
                  <p className="text-foreground text-xs font-medium uppercase tracking-wide">
                    {fieldLabels.yearlyContribution.label}
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-gray-950">
                    {existingSource?.monthlyContributionAmount != null
                      ? formatCurrency(existingSource.monthlyContributionAmount * 12)
                      : '—'}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 px-3 py-2.5">
                  <p className="text-foreground text-xs font-medium uppercase tracking-wide">
                    {fieldLabels.rateOfReturn.label}
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-gray-950">
                    {existingSource?.annualRateOfReturn != null
                      ? `${Math.round(existingSource.annualRateOfReturn * 100)}%`
                      : '—'}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 px-3 py-2.5">
                  <p className="text-foreground text-xs font-medium uppercase tracking-wide">
                    {fieldLabels.taxRate.label}
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-gray-950">
                    {existingSource?.gainsTaxRate != null
                      ? `${Math.round(existingSource.gainsTaxRate * 100)}%`
                      : '—'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="px-5 pb-5 pt-4">{formContent}</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
